import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const StudentPhotoUpload = ({ student, onPhotoUpdate }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (file) => {
    if (!file) return;
    
    // Validate file type
    if (!file?.type?.startsWith('image/')) {
      alert('Please select a valid image file');
      return;
    }

    // Validate file size (5MB max)
    if (file?.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    setIsUploading(true);
    
    // Simulate upload process
    setTimeout(() => {
      const reader = new FileReader();
      reader.onload = (e) => {
        onPhotoUpdate?.(e?.target?.result);
        setIsUploading(false);
      };
      reader?.readAsDataURL(file);
    }, 1500);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setDragActive(false);
    const file = e?.dataTransfer?.files?.[0];
    handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setDragActive(false);
  };

  const handleFileInputChange = (e) => {
    const file = e?.target?.files?.[0];
    handleFileSelect(file);
  };

  const handleRemovePhoto = () => {
    onPhotoUpdate?.(null);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <h3 className="font-semibold text-card-foreground mb-4">Student Photo</h3>
      {student?.photo ? (
        <div className="space-y-4">
          <div className="relative w-32 h-32 mx-auto">
            <Image
              src={student?.photo}
              alt={`${student?.firstName} ${student?.lastName}`}
              className="w-full h-full object-cover rounded-lg border-2 border-border"
            />
            {isUploading && (
              <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center">
                <Icon name="Loader2" size={24} className="text-white animate-spin" />
              </div>
            )}
          </div>
          
          <div className="flex gap-2 justify-center">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef?.current?.click()}
              disabled={isUploading}
              iconName="Upload"
              iconPosition="left"
            >
              Update
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRemovePhoto}
              disabled={isUploading}
              iconName="Trash2"
              iconPosition="left"
            >
              Remove
            </Button>
          </div>
        </div>
      ) : (
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors duration-200 ${
            dragActive 
              ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          {isUploading ? (
            <div className="space-y-3">
              <Icon name="Loader2" size={32} className="text-primary mx-auto animate-spin" />
              <p className="text-sm text-muted-foreground">Uploading photo...</p>
            </div>
          ) : (
            <div className="space-y-3">
              <Icon name="ImagePlus" size={32} className="text-muted-foreground mx-auto" />
              <div>
                <p className="text-sm font-medium text-foreground">Upload student photo</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Drag and drop or click to select
                </p>
                <p className="text-xs text-muted-foreground">
                  PNG, JPG up to 5MB
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef?.current?.click()}
                iconName="Upload"
                iconPosition="left"
              >
                Select Photo
              </Button>
            </div>
          )}
        </div>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileInputChange}
        className="hidden"
      />
    </div>
  );
};

export default StudentPhotoUpload;